import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Camera, ArrowRight, ArrowLeft, Loader2, Crown, X } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import api, { formatApiErrorDetail } from "../lib/api";

const CHOICES = {
  scalp_itching: ["none", "mild", "moderate", "severe"],
  dandruff: ["none", "some", "heavy"],
};

function OptionGroup({ value, onChange, options, testid }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="flex flex-wrap gap-3" data-testid={testid}>
      {options.map((o) => (
        <label
          key={o}
          className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm capitalize transition-colors ${
            value === o ? "border-gold bg-gold/10 text-gold" : "border-charcoal-border text-cream hover:border-gold/40"
          }`}
        >
          <RadioGroupItem value={o} className="sr-only" />
          {o}
        </label>
      ))}
    </RadioGroup>
  );
}

export default function Scan() {
  const location = useLocation();
  const navigate = useNavigate();
  const paymentId = location.state?.paymentId;
  const fileRef = useRef(null);

  const [step, setStep] = useState(0);
  const [photo, setPhoto] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [q, setQ] = useState({
    time_since_service: "",
    wash_frequency: "",
    scalp_itching: "none",
    dandruff: "none",
    client_concerns: "",
  });

  useEffect(() => {
    if (!paymentId) {
      toast.error("Please pay the scan fee first.");
      navigate("/payment", { replace: true });
    }
  }, [paymentId, navigate]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(jpeg|png|webp|jpg)/.test(file.type)) {
      toast.error("Please upload a JPEG, PNG or WEBP image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post("/scans", {
        payment_id: paymentId,
        photo_base64: photo,
        questionnaire: q,
      });
      toast.success("Your report is ready.");
      navigate(`/report/${data.scan_id}`, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setAnalyzing(false);
    }
  };

  const steps = [
    { title: "Capture your scalp", key: "photo" },
    { title: "Your routine", key: "routine" },
    { title: "Scalp symptoms", key: "symptoms" },
    { title: "Your concerns & review", key: "review" },
  ];

  const canNext = () => {
    if (step === 0) return !!photo;
    if (step === 1) return q.time_since_service.trim() && q.wash_frequency.trim();
    return true;
  };

  if (analyzing) {
    return (
      <div className="min-h-screen bg-charcoal text-cream">
        <Navbar />
        <div className="mx-auto flex max-w-md flex-col items-center px-6 py-32 text-center">
          <div className="relative mb-8">
            <Crown className="h-12 w-12 text-gold" strokeWidth={1} />
            <div className="absolute inset-0 animate-ping opacity-30"><Crown className="h-12 w-12 text-gold" strokeWidth={1} /></div>
          </div>
          <h1 className="font-heading text-3xl text-cream" data-testid="analyzing-title">Scalp Analyzing</h1>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="analyzing-message">
            Please wait — it can take up to 5 minutes. Don't close the page.
          </p>
          <Loader2 className="mt-8 h-6 w-6 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />
      <div className="fixed left-0 top-[65px] z-40 h-0.5 w-full bg-charcoal-border">
        <div className="h-full bg-gold transition-all duration-500" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
      </div>

      <div className="mx-auto max-w-2xl px-6 py-14">
        <span className="text-xs uppercase tracking-[0.3em] text-gold">Step {step + 1} of {steps.length}</span>
        <h1 className="mt-2 font-heading text-4xl text-cream">{steps[step].title}</h1>

        <div className="mt-10 min-h-[320px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35 }}
            >
              {step === 0 && (
                <div>
                  {photo ? (
                    <div className="relative overflow-hidden rounded-2xl border border-charcoal-border">
                      <img src={photo} alt="scalp preview" className="max-h-96 w-full object-cover" data-testid="photo-preview" />
                      <button
                        onClick={() => setPhoto(null)}
                        className="absolute right-3 top-3 rounded-full bg-black/70 p-2 text-cream hover:text-gold"
                        data-testid="remove-photo-btn"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="flex w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-charcoal-border py-20 transition-colors hover:border-gold/50"
                      data-testid="upload-photo-zone"
                    >
                      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-gold/30 text-gold">
                        <Upload className="h-6 w-6" strokeWidth={1.5} />
                      </span>
                      <p className="font-heading text-2xl text-cream">Upload or capture</p>
                      <p className="mt-1 text-sm text-muted-foreground">A close-up of your scalp and loc roots · JPEG, PNG, WEBP</p>
                      <span className="mt-4 flex items-center gap-2 text-xs text-gold"><Camera className="h-3.5 w-3.5" /> Tap to open camera or gallery</span>
                    </button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="photo-file-input" />
                </div>
              )}

              {step === 1 && (
                <div className="space-y-8">
                  <div>
                    <Label className="mb-3 block text-cream">When was the last time you had a service?</Label>
                    <Input
                      value={q.time_since_service} onChange={(e) => setQ({ ...q, time_since_service: e.target.value })}
                      placeholder="e.g. 3 weeks ago, last month" className="border-charcoal-border bg-charcoal-surface text-cream focus-visible:ring-gold"
                      data-testid="last-service-input"
                    />
                  </div>
                  <div>
                    <Label className="mb-3 block text-cream">How often do you wash your hair?</Label>
                    <Input
                      value={q.wash_frequency} onChange={(e) => setQ({ ...q, wash_frequency: e.target.value })}
                      placeholder="e.g. Weekly, every 2 weeks" className="border-charcoal-border bg-charcoal-surface text-cream focus-visible:ring-gold"
                      data-testid="wash-frequency-input"
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8">
                  <div>
                    <Label className="mb-3 block text-cream">Scalp itching?</Label>
                    <OptionGroup value={q.scalp_itching} onChange={(v) => setQ({ ...q, scalp_itching: v })} options={CHOICES.scalp_itching} testid="itching-group" />
                  </div>
                  <div>
                    <Label className="mb-3 block text-cream">Dandruff?</Label>
                    <OptionGroup value={q.dandruff} onChange={(v) => setQ({ ...q, dandruff: v })} options={CHOICES.dandruff} testid="dandruff-group" />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-8">
                  <div>
                    <Label className="mb-3 block text-cream">Anything specific you're concerned about? (optional)</Label>
                    <Textarea
                      value={q.client_concerns} onChange={(e) => setQ({ ...q, client_concerns: e.target.value })}
                      placeholder="Tell us in your own words — e.g. my edges feel thin, my scalp gets flaky a week after washing…"
                      className="border-charcoal-border bg-charcoal-surface text-cream focus-visible:ring-gold"
                      data-testid="client-concerns-input"
                    />
                  </div>
                  <div className="rounded-xl border border-charcoal-border bg-charcoal-surface p-6 text-sm">
                    <p className="mb-3 font-heading text-xl text-cream">Review</p>
                    <ul className="space-y-1.5 text-muted-foreground">
                      <li>Last service: <span className="text-cream">{q.time_since_service || "—"}</span></li>
                      <li>Wash frequency: <span className="text-cream">{q.wash_frequency || "—"}</span></li>
                      <li>Scalp itching: <span className="capitalize text-cream">{q.scalp_itching}</span></li>
                      <li>Dandruff: <span className="capitalize text-cream">{q.dandruff}</span></li>
                    </ul>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-between">
          <Button
            variant="ghost" disabled={step === 0}
            className="rounded-full text-muted-foreground hover:text-cream disabled:opacity-0"
            onClick={() => setStep((s) => s - 1)} data-testid="wizard-back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>

          {step < steps.length - 1 ? (
            <Button
              disabled={!canNext()}
              className="group rounded-full bg-gold px-7 py-5 text-charcoal hover:bg-gold/90"
              onClick={() => setStep((s) => s + 1)} data-testid="wizard-next-btn"
            >
              Continue <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          ) : (
            <Button
              className="rounded-full bg-gold px-7 py-5 text-charcoal hover:bg-gold/90"
              onClick={submit} data-testid="submit-scan-btn"
            >
              <Crown className="mr-2 h-4 w-4" /> Generate report
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
